import React, { useState } from "react";
import UserAvatar from "./UserAvatar";

// Color options for customization
const colorOptions = {
  hair: [
    "#6D4C41",
    "#3E2723",
    "#000000",
    "#E65100",
    "#FFA000",
    "#FDD835",
    "#C2185B",
  ],
  shirt: [
    "#2196F3",
    "#F44336",
    "#4CAF50",
    "#9C27B0",
    "#FF9800",
    "#607D8B",
    "#FFEB3B",
  ],
  eyes: ["#795548", "#3E2723", "#0D47A1", "#1B5E20", "#006064", "#E65100"],
  skin: ["#FFF9C4", "#FFE0B2", "#FFCCBC", "#D7CCC8", "#F8BBD0", "#C8E6C9"],
};

const AvatarCustomizer = ({ onComplete, onCancel }) => {
  const [avatarOptions, setAvatarOptions] = useState({
    hairType: "medium", // Default to medium
    hairColor: colorOptions.hair[0],
    shirtColor: colorOptions.shirt[0],
    eyeColor: colorOptions.eyes[0],
    skinColor: colorOptions.skin[0],
  });

  const [previewUser, setPreviewUser] = useState({
    avatar_hair_type: "medium", // Default to medium
    avatar_hair: colorOptions.hair[0],
    avatar_shirt: colorOptions.shirt[0],
    avatar_eyes: colorOptions.eyes[0],
    avatar_skin: colorOptions.skin[0],
  });

  // Update avatar option and preview
  const updateOption = (type, value) => {
    setAvatarOptions((prev) => {
      const updated = { ...prev, [type]: value };

      // Update preview user as well
      setPreviewUser({
        avatar_hair_type: updated.hairType,
        avatar_hair: updated.hairColor,
        avatar_shirt: updated.shirtColor,
        avatar_eyes: updated.eyeColor,
        avatar_skin: updated.skinColor,
      });

      return updated;
    });
  };

  // Handle completion
  const handleComplete = (e) => {
    if (e) e.stopPropagation();

    // Log the data we're sending for debugging
    console.log("Avatar options being saved:", avatarOptions);

    // Ensure we're passing the current avatar options
    if (onComplete && typeof onComplete === "function") {
      onComplete(avatarOptions);
    }
  };

  return (
    <div
      className="avatar-customizer-overlay"
      onClick={(e) => e.stopPropagation()} // Stop clicks from reaching elements underneath
    >
      <style jsx="true">{`
        .avatar-customizer-overlay {
          position: relative;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: transparent;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 5000; /* Ensure it's above everything */
          padding: 20px;
          height: 100vh; /* Make sure it covers full viewport height */
          width: 100vw; /* Make sure it covers full viewport width */
        }

        .avatar-customizer {
          background-color: #fff;
          border-radius: 16px;
          padding: 20px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          /* Remove position: fixed and use the flex centering from the parent */
          position: fixed;
          top: 0;
          left: 0;
          transform: none;
        }

        .avatar-preview {
          display: flex;
          justify-content: center;
          margin: 20px 0;
        }

        .option-section {
          margin-bottom: 20px;
        }

        .option-title {
          font-weight: bold;
          margin-bottom: 10px;
          color: #333;
          font-family: "Inter", sans-serif;
          font-size: 14px;
        }

        .color-options,
        .gender-options {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: center;
        }

        .color-option {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          cursor: pointer;
          transition: transform 0.2s;
          border: 2px solid #ddd;
        }

        .color-option.selected {
          transform: scale(1.2);
          border: 2px solid #000;
          box-shadow: 0 0 8px rgba(0, 0, 0, 0.4);
        }

        .color-option:hover {
          transform: scale(1.1);
        }

        .gender-option {
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          border: 2px solid #ddd;
          background-color: #f5f5f5;
          font-family: "Inter", sans-serif;
          font-size: 12px;
        }

        .gender-option.selected {
          background-color: #2196f3;
          color: white;
          border-color: #0d47a1;
        }

        .action-buttons {
          display: flex;
          justify-content: center;
          gap: 15px;
          margin-top: 20px;
        }

        .action-button {
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: bold;
          border: none;
          font-family: "Inter", sans-serif;
          font-size: 14px;
          transition: all 0.2s;
        }

        .complete-button {
          background-color: #4caf50;
          color: white;
        }

        .complete-button:hover {
          background-color: #388e3c;
        }

        .cancel-button {
          background-color: #9e9e9e;
          color: white;
        }

        .cancel-button:hover {
          background-color: #757575;
        }

        .customizer-title {
          text-align: center;
          font-family: "Inter", sans-serif;
          margin-bottom: 20px;
          color: #333;
          font-weight: 700;
        }
      `}</style>

      <div
        className="avatar-customizer"
        onClick={(e) => e.stopPropagation()} // Add extra protection
      >
        <h2 className="customizer-title">Create Your Knight</h2>

        <div className="avatar-preview">
          <UserAvatar user={previewUser} size={100} />
        </div>

        <div className="option-section">
          <div className="option-title">Hair Style</div>
          <div className="gender-options">
            <div
              className={`gender-option ${
                avatarOptions.hairType === "short" ? "selected" : ""
              }`}
              onClick={() => updateOption("hairType", "short")}
            >
              Short
            </div>
            <div
              className={`gender-option ${
                avatarOptions.hairType === "medium" ? "selected" : ""
              }`}
              onClick={() => updateOption("hairType", "medium")}
            >
              Medium
            </div>
            <div
              className={`gender-option ${
                avatarOptions.hairType === "long" ? "selected" : ""
              }`}
              onClick={() => updateOption("hairType", "long")}
            >
              Long
            </div>
          </div>
        </div>

        <div className="option-section">
          <div className="option-title">Hair Color</div>
          <div className="color-options">
            {colorOptions.hair.map((color, index) => (
              <div
                key={`hair-${index}`}
                className={`color-option ${
                  avatarOptions.hairColor === color ? "selected" : ""
                }`}
                style={{ backgroundColor: color }}
                onClick={() => updateOption("hairColor", color)}
              />
            ))}
          </div>
        </div>

        <div className="option-section">
          <div className="option-title">Shirt Color</div>
          <div className="color-options">
            {colorOptions.shirt.map((color, index) => (
              <div
                key={`shirt-${index}`}
                className={`color-option ${
                  avatarOptions.shirtColor === color ? "selected" : ""
                }`}
                style={{ backgroundColor: color }}
                onClick={() => updateOption("shirtColor", color)}
              />
            ))}
          </div>
        </div>

        <div className="option-section">
          <div className="option-title">Eye Color</div>
          <div className="color-options">
            {colorOptions.eyes.map((color, index) => (
              <div
                key={`eyes-${index}`}
                className={`color-option ${
                  avatarOptions.eyeColor === color ? "selected" : ""
                }`}
                style={{ backgroundColor: color }}
                onClick={() => updateOption("eyeColor", color)}
              />
            ))}
          </div>
        </div>

        <div className="option-section">
          <div className="option-title">Skin Color</div>
          <div className="color-options">
            {colorOptions.skin.map((color, index) => (
              <div
                key={`skin-${index}`}
                className={`color-option ${
                  avatarOptions.skinColor === color ? "selected" : ""
                }`}
                style={{ backgroundColor: color }}
                onClick={() => updateOption("skinColor", color)}
              />
            ))}
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="action-button complete-button"
            onClick={(e) => {
              e.stopPropagation(); // Stop propagation
              handleComplete(e);
            }}
          >
            Complete
          </button>
          <button
            className="action-button cancel-button"
            onClick={(e) => {
              e.stopPropagation(); // Stop propagation
              if (onCancel) onCancel();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarCustomizer;
